import {
  Application,
  BankApplication,
  BankApplicationStatus,
  Query,
  Typenames,
} from '@varo/graphql-schema-types';
import { useApplicationTrackErrorEvents } from 'features/application/hooks/useApplicationTrackErrorEvents';
import { useEffect, useState } from 'react';
import CustomerApplicationOrigination from 'services/graphql/queries/CustomerApplicationOrigination';
import { RequestPolicy, useQuery } from 'urql';

interface useCustomerApplicationOriginationArgs {
  requestPolicy?: RequestPolicy;
  sourceName?: string;
}

const useCustomerApplicationOrigination = ({
  requestPolicy = 'cache-and-network',
  sourceName,
}: useCustomerApplicationOriginationArgs = {}) => {
  const [response, reExecuteQuery] = useQuery<Query>({
    query: CustomerApplicationOrigination,
    requestPolicy,
  });
  const { data, fetching, error } = response;
  const [application, setApplication] = useState<Application | null>(null);
  const [hasApplicationLoaded, setHasApplicationLoaded] =
    useState<boolean>(false);
  const [isReapplication, setIsReapplication] = useState<boolean>(false);
  const { logErrorEvent } = useApplicationTrackErrorEvents();

  useEffect(() => {
    if (error && sourceName) {
      logErrorEvent(error.message, sourceName);
    }
  }, [error, logErrorEvent, sourceName]);

  useEffect(() => {
    data?.applications?.forEach((app) => {
      if (app !== null) {
        if (
          '__typename' in app &&
          app.__typename === Typenames.BankApplication
        ) {
          const customerApplication = app as BankApplication;
          // set isReapplication to true if any application is eligible for reapply
          if (customerApplication.eligibleForReapply === true) {
            setIsReapplication(true);
          }
          // do not use or prefill data based on declined application with eligibility to reapply
          // new application will be created in personal info screen
          if (
            customerApplication.eligibleForReapply !== true &&
            customerApplication.bankApplicationStatus !==
              BankApplicationStatus.Declined
          ) {
            setApplication(customerApplication);
          }
          setHasApplicationLoaded(true);
        }
      }
    });
  }, [data]);

  return {
    application,
    fetching,
    error,
    reExecuteQuery,
    isReapplication,
    hasApplicationLoaded,
  };
};

export default useCustomerApplicationOrigination;
